import { useEffect, useRef } from "react";
import { useTranslation } from "react-i18next";

export default function AdvantageSection() {
  const sectionRef = useRef<HTMLDivElement>(null);
  const { t } = useTranslation("home");

  useEffect(() => {
    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            entry.target.classList.add("opacity-100", "translate-y-0");
            entry.target.classList.remove("opacity-0", "translate-y-6");
          }
        });
      },
      { threshold: 0.15 }
    );

    const els = sectionRef.current?.querySelectorAll(".animate-in");
    els?.forEach((el) => observer.observe(el));
    return () => observer.disconnect();
  }, []);

  const advantages = [
    {
      titleKey: "advantage.item1.title",
      descKey: "advantage.item1.desc",
      icon: "ri-shield-check-line",
    },
    {
      titleKey: "advantage.item2.title",
      descKey: "advantage.item2.desc",
      icon: "ri-cpu-line",
    },
    {
      titleKey: "advantage.item3.title",
      descKey: "advantage.item3.desc",
      icon: "ri-globe-line",
    },
    {
      titleKey: "advantage.item4.title",
      descKey: "advantage.item4.desc",
      icon: "ri-palette-line",
    },
  ];

  return (
    <section
      id="advantage"
      ref={sectionRef}
      className="bg-white py-20 md:py-28"
    >
      <div className="px-6 md:px-12 lg:px-20">
        {/* Header */}
        <div className="text-center mb-16 animate-in opacity-0 translate-y-6 transition-all duration-700">
          <h2 className="font-serif text-3xl md:text-4xl lg:text-5xl text-ax-dark font-bold mb-4">
            {t("advantage.title")}
          </h2>
          <p className="text-ax-dark/50 text-sm md:text-base max-w-xl mx-auto">
            {t("advantage.subtitle")}
          </p>
        </div>

        {/* Advantage Grid */}
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mb-16">
          {advantages.map((a, i) => (
            <div
              key={a.titleKey}
              className="animate-in opacity-0 translate-y-6 transition-all duration-700 flex items-start gap-5 p-6 md:p-8 rounded-2xl border border-ax-silver-light hover:border-ax-red/30 hover:shadow-sm group"
              style={{ transitionDelay: `${200 + i * 150}ms` }}
            >
              <div className="w-12 h-12 bg-ax-red/10 rounded-xl flex items-center justify-center flex-shrink-0 group-hover:bg-ax-red transition-colors">
                <i className={`${a.icon} text-ax-red text-xl group-hover:text-white transition-colors`} />
              </div>
              <div>
                <h3 className="font-serif text-lg font-bold text-ax-dark mb-2">
                  {t(a.titleKey)}
                </h3>
                <p className="text-sm text-ax-dark/50 leading-relaxed">
                  {t(a.descKey)}
                </p>
              </div>
            </div>
          ))}
        </div>

        {/* Product showcase banner */}
        <div className="mt-16 animate-in opacity-0 translate-y-6 transition-all duration-700 delay-1000">
          <div className="flex items-center justify-center gap-4 md:gap-8 py-8 px-6 bg-ax-light rounded-2xl overflow-hidden">
            <img
              src="https://readdy.ai/api/search-image?query=Collection%20of%20premium%20tech%20storage%20products%20arranged%20in%20a%20row%2C%20DDR%20memory%20chips%20USB%20drives%20and%20NAS%20server%20devices%2C%20silver%20anodized%20aluminum%20finishes%2C%20clean%20white%20background%2C%20Tesla-style%20minimalist%20industrial%20design%2C%20professional%20product%20lineup%20photography%2C%20even%20lighting&width=1200&height=300&seq=ax-products-1&orientation=landscape"
              alt="AX Product Lineup"
              className="w-full h-[180px] md:h-[220px] object-cover object-top rounded-xl"
            />
          </div>
        </div>
      </div>
    </section>
  );
}